import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import {
  Typography,
  Select,
  message as antdMessage,
  Tooltip,
  Button,
  Spin,
  Dropdown,
  Modal,
  Switch,
} from 'antd';
import dayjs from 'dayjs';
import {
  DatabaseOutlined,
  RobotOutlined,
  UserOutlined,
  SendOutlined,
  CodeOutlined,
  DeleteOutlined,
  MoreOutlined,
  ShareAltOutlined,
  PlusOutlined,
  ApiOutlined,
  BarChartOutlined,
  EditOutlined,
  PlayCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { Input } from 'antd';
import { flushSync } from 'react-dom';
import './Chat.css';
import AssistantMarkdown from '../components/chat/AssistantMarkdown';
import ChatShareDialog from '../components/chat/ChatShareDialog';
import { useShareChrome } from '../contexts/ShareChromeContext';
import PipelineTracePanel from '../components/chat/PipelineTracePanel';
import StreamingPipelineStatus from '../components/chat/StreamingPipelineStatus';
import {
  sendChatMessageStream,
  fetchAssistant,
  getDataCollections,
  sendRAGChatMessageStream,
  saveChatMessage,
  getChatMessages,
  submitMessageFeedback,
  removeMessageFeedback,
  clearChatHistory,
  getAssistantStatistics,
  runAssistant,
  stopAssistant,
  getPipelineTraceVisibility,
} from '../services/api';
import { relativeTime } from '../utils/relativeTime';
import { assistantDisplayImageSrc } from '../utils/assistantVisual';
import AssistantStatisticsContent from '../components/assistants/AssistantStatisticsContent';
import { copyText } from '../utils/clipboard';
import { fullDateTime } from '../utils/dateTimeFormat';
import { MessageActions } from '../components/chat/MessageActionsBar';
import ChatMessageAvatar from '../components/chat/ChatMessageAvatar';
import MessageInlineActions from '../components/chat/MessageInlineActions';
import MessageWithParts from '../chat-ui/MessageWithParts';
import OtpOpenMailButton from '../chat-ui/components/transport/OtpOpenMailButton';
import { resolveOtpMailTarget } from '../chat-ui/components/transport/otpMail';
import { assistantMessageNeedsFallback, inferAssistantFallback } from '../chat-ui/assistantFallback';
import UserGateBar from '../chat-ui/components/UserGateBar';
import { formatUiActionLabel, upsertUiPart, applyUiPatchToMessages, filterDisplayableUiParts, findInteractionBarPart, bubbleUiParts } from '../chat-ui/uiActionLabel';
import { buildResumePayload, useAgentUserGate } from '../hooks/useAgentUserGate';
import '../chat-ui/components/ui-parts.css';
import { consumeChatNdjsonStream, subscribeLiveSession } from '../services/api/streamNdjson';
import { readPublicShare, sendPublicShare, startNewPublicShare } from '../services/api/chatShares';
import { sendAgentChatStream, listAssistantConnectors, listAssistantMcpServers } from '../services/api/agents';
import useChatEngineToggles from '../hooks/useChatEngineToggles';
import {
  RAG_STRATEGY_AGENT_LED,
  RAG_STRATEGY_LEGACY,
  buildEngineMetadata,
  formatEngineStatusLine,
} from '../utils/chatEngineRouting';

/** Stable wrapper while tokens stream. */
function StreamingAssistantMarkdown({ content }) {
  return (
    <div className="chat-stream-markdown-wrap">
      <AssistantMarkdown content={content} />
    </div>
  );
}

function createChatSessionId() {
  return crypto.randomUUID?.() || `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
}

function conversationStartersFor(assistant) {
  const raw = assistant?.conversation_starters
    || assistant?.chat_settings?.conversation_starters
    || [];
  if (!Array.isArray(raw)) return [];
  return raw.filter((item) => typeof item === 'string' && item.trim()).slice(0, 8);
}

function Chat() {
  const { assistantId, token } = useParams();
  const shareMode = Boolean(token);
  const { setChatOnly } = useShareChrome();
  const { keycloak, initialized } = useKeycloak();
  const navigate = useNavigate();

  const [input, setInput] = useState('');
  const [assistant, setAssistant] = useState(null);
  const [assistantLoading, setAssistantLoading] = useState(true);
  const [collections, setCollections] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [sessionId, setSessionId] = useState(createChatSessionId);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [, setTick] = useState(0);
  const [clearHistoryModalVisible, setClearHistoryModalVisible] = useState(false);
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [statsDateRange, setStatsDateRange] = useState(() => [dayjs().subtract(30, 'day'), dayjs()]);
  const [pipelineTraceVisibility, setPipelineTraceVisibility] = useState(null);
  const [hasAgentTools, setHasAgentTools] = useState(false);
  const [agentSessionId, setAgentSessionId] = useState(null);
  const [chatDir, setChatDir] = useState('ltr');
  const [shareOpen, setShareOpen] = useState(false);
  const [shareState, setShareState] = useState(shareMode ? 'loading' : 'open');
  const [shareComposer, setShareComposer] = useState('open');
  const [allowEmbed, setAllowEmbed] = useState(false);

  const sharePromptApplied = useRef(false);
  useEffect(() => {
    if (!shareMode || sharePromptApplied.current) return;
    sharePromptApplied.current = true;
    const text = (new URLSearchParams(window.location.search).get('q') || '').trim();
    if (text) setInput(text.slice(0, 4000));
  }, [shareMode]);

  const messagesEndRef = useRef(null);
  const lastMessageTopRef = useRef(null);
  const inputRef = useRef(null);
  const patchQueueRef = useRef(Promise.resolve());
  const patchDepthRef = useRef(0);

  const {
    dismissGate,
    markResuming,
    isResumeAction,
    isGateCancelAction,
    findGatePart,
  } = useAgentUserGate({ setMessages });

  const availableCollections = useMemo(
    () => collections.filter((c) => c.embeddings_status === 'completed'),
    [collections],
  );

  const {
    collectionAttached,
    setCollectionAttached,
    toolsEnabled,
    setToolsEnabled,
    ragStrategy,
    setRagStrategy,
    selectedCollection,
    setSelectedCollection,
    enginePath,
    showRagStrategy,
    toggles,
  } = useChatEngineToggles(assistant, assistantId, hasAgentTools, availableCollections);

  const engineStatusLine = useMemo(() => {
    const name = collections.find((c) => c.id === selectedCollection)?.name;
    return formatEngineStatusLine(toggles, enginePath, name);
  }, [collections, selectedCollection, toggles, enginePath]);

  const agentRequestFields = useCallback(() => ({
    session_id: sessionId,
    agent_session_id: agentSessionId,
    collection_attached: Boolean(collectionAttached && selectedCollection),
    collection_id: selectedCollection || undefined,
    tools_enabled: toolsEnabled,
    rag_strategy: enginePath.ragStrategy || RAG_STRATEGY_LEGACY,
  }), [
    sessionId, agentSessionId, collectionAttached, selectedCollection,
    toolsEnabled, enginePath.ragStrategy,
  ]);

  const scrollToLatestMessage = useCallback(() => {
    requestAnimationFrame(() => {
      lastMessageTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const prevScrollKeyRef = useRef('');
  useEffect(() => {
    const last = messages[messages.length - 1];
    const scrollKey = JSON.stringify({
      count: messages.length,
      lastRole: last?.role,
      lastStreaming: last?.streaming,
      lastContentLen: (last?.content || '').length,
      lastIsError: Boolean(last?.isError),
    });
    if (scrollKey === prevScrollKeyRef.current) return;
    prevScrollKeyRef.current = scrollKey;
    scrollToLatestMessage();
  }, [messages, scrollToLatestMessage]);

  // Relative timestamps refresh
  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!shareMode) return undefined;
    let meta = document.querySelector('meta[name="robots"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      document.head.appendChild(meta);
    }
    const previous = meta.getAttribute('content');
    meta.setAttribute('content', 'noindex, nofollow, noarchive, nosnippet');
    return () => {
      if (created) meta.remove();
      else if (previous) meta.setAttribute('content', previous);
    };
  }, [shareMode]);

  useEffect(() => {
    if (!shareMode || !initialized) return undefined;
    let cancelled = false;
    (async () => {
      setAssistantLoading(true);
      try {
        const data = await readPublicShare(token, keycloak?.authenticated ? keycloak : null);
        if (cancelled) return;
        setShareState(data.state || 'not_found');
        setChatOnly(Boolean(data.chat_only));
        setShareComposer(data.composer || 'closed');
        setAllowEmbed(Boolean(data.allow_embed));
        setAssistant(data.assistant ? { ...data.assistant, router_mode: false } : null);
        setMessages((data.messages || []).map((message) => ({
          role: message.role,
          content: message.content,
          ui: message.ui || null,
          ui_parts: message.ui_parts || null,
          actions_enabled: Boolean(message.actions_enabled),
        })));
      } catch (err) {
        if (!cancelled) setShareState('not_found');
      } finally {
        if (!cancelled) {
          setAssistantLoading(false);
          setHistoryLoaded(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [shareMode, token, initialized, keycloak, setChatOnly]);

  useEffect(() => () => setChatOnly(false), [setChatOnly]);

  // Load assistant, collections and chat history
  useEffect(() => {
    if (shareMode || !initialized || !keycloak?.authenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const v = await getPipelineTraceVisibility(keycloak);
        if (!cancelled) setPipelineTraceVisibility(v);
      } catch (_) {
        if (!cancelled) setPipelineTraceVisibility(null);
      }
    })();
    return () => { cancelled = true; };
  }, [initialized, keycloak]);

  useEffect(() => {
    let cancelled = false;
    if (shareMode || !initialized || !keycloak?.authenticated || !assistantId) return;

    (async () => {
      setAssistantLoading(true);
      try {
        const data = await fetchAssistant(keycloak, assistantId);
        if (!cancelled) setAssistant(data);
      } catch (err) {
        if (!cancelled) { setAssistant(null); antdMessage.error(err?.message || 'Failed to load assistant'); }
      } finally { if (!cancelled) setAssistantLoading(false); }
    })();

    (async () => {
      setCollectionsLoading(true);
      try { const data = await getDataCollections(keycloak); if (!cancelled) setCollections(data); }
      catch (_) { /* */ }
      finally { if (!cancelled) setCollectionsLoading(false); }
    })();

    (async () => {
      try {
        const history = await getChatMessages(keycloak, assistantId);
        if (!cancelled && history.length) {
          const mapped = history.map(m => ({
            id: m.id, role: m.role, content: m.content, isError: m.is_error,
            timestamp: m.created_at, feedback: m.feedback || null,
            pipeline_trace: m.pipeline_trace || null,
            session_id: m.session_id,
            ui: m.message_metadata?.ui || null,
            ui_parts: m.message_metadata?.ui_parts || null,
            consulted: m.message_metadata?.consulted || null,
          }));
          let stored = null;
          try { stored = sessionStorage.getItem(`focusml-chat-session:${assistantId}`); } catch (_) { /* */ }
          if (stored) {
            setSessionId(stored);
            setMessages(mapped.filter((item) => item.session_id === stored));
          } else {
            setMessages(mapped);
            const latestSession = mapped[mapped.length - 1]?.session_id;
            if (latestSession) setSessionId(latestSession);
          }
        }
      } catch (_) { /* first visit */ }
      finally { if (!cancelled) setHistoryLoaded(true); }
    })();

    return () => { cancelled = true; };
  }, [shareMode, assistantId, initialized, keycloak]);

  useEffect(() => {
    if (shareMode || !initialized || !keycloak?.authenticated || !assistantId) return;
    let cancelled = false;
    (async () => {
      try {
        const [connectors, mcpLinks] = await Promise.all([
          listAssistantConnectors(keycloak, assistantId),
          listAssistantMcpServers(keycloak, assistantId),
        ]);
        const hasAgentTools =
          connectors?.some((l) => l.enabled) || mcpLinks?.some((l) => l.enabled);
        if (!cancelled) setHasAgentTools(Boolean(hasAgentTools));
      } catch (_) {
        if (!cancelled) setHasAgentTools(false);
      }
    })();
    return () => { cancelled = true; };
  }, [assistantId, initialized, keycloak]);

  useEffect(() => {
    if (!toolsEnabled || !hasAgentTools || !keycloak?.token) return undefined;
    const unsub = subscribeLiveSession(sessionId, keycloak.token, {
      onUiPatch: (patch) => {
        setMessages((prev) => applyUiPatchToMessages(prev, patch));
      },
      onProactiveMessage: (content) => {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content, timestamp: new Date().toISOString() },
        ]);
      },
    });
    return unsub;
  }, [toolsEnabled, hasAgentTools, sessionId, keycloak?.token]);

  useEffect(() => {
    if (shareMode) return;
    setMessages([]);
    setHistoryLoaded(false);
  }, [assistantId, shareMode]);

  const persistMessage = useCallback(async (role, content, isError = false, pipelineTrace = null, messageMetadata = null) => {
    try {
      return await saveChatMessage(keycloak, {
        assistantId: assistant?.id,
        sessionId,
        role,
        content,
        isError,
        pipeline_trace: pipelineTrace || undefined,
        message_metadata: messageMetadata || undefined,
      });
    } catch (_) { return null; }
  }, [keycloak, assistant, sessionId]);

  const buildMessageMetadata = useCallback((ui, uiParts, engineMeta) => {
    const meta = {};
    if (ui) {
      meta.ui = {
        actions: ui.actions,
        progress: ui.progress,
        handoff: ui.handoff,
      };
    }
    if (uiParts?.length) {
      meta.ui_parts = uiParts;
    }
    if (engineMeta) {
      Object.assign(meta, engineMeta);
    }
    if (engineMeta?.consulted) {
      meta.consulted = engineMeta.consulted;
    }
    if (engineMeta?.pinned_specialist_id) {
      meta.pinned_specialist_id = engineMeta.pinned_specialist_id;
      meta.pinned_agent_session_id = engineMeta.pinned_agent_session_id ?? null;
    }
    return Object.keys(meta).length ? meta : undefined;
  }, []);

  const applyUiToLastMessage = useCallback((ui) => {
    if (!ui) return;
    setMessages((prev) => {
      const next = [...prev];
      const i = next.length - 1;
      if (i >= 0 && next[i].role === 'assistant') {
        next[i] = {
          ...next[i],
          ui: {
            actions: ui.actions || [],
            progress: ui.progress,
            handoff: ui.handoff,
          },
        };
      }
      return next;
    });
  }, []);

  const handleInlineAction = useCallback(async (action) => {
    if (action.id?.startsWith('link:')) {
      window.open(action.id.slice(5), '_blank', 'noopener,noreferrer');
    }
  }, []);

  const sendShare = useCallback(async (body, { showUser = false } = {}) => {
    if (!shareMode || isSending || shareComposer !== 'open') return;
    setIsSending(true);
    setInput('');
    if (showUser && body.content) {
      flushSync(() => {
        setMessages((prev) => [...prev, {
          role: 'user',
          content: body.content,
          timestamp: new Date().toISOString(),
          actions_enabled: false,
        }]);
      });
    }
    const placeholder = {
      role: 'assistant',
      content: '',
      streaming: true,
      actions_enabled: true,
      timestamp: new Date().toISOString(),
      ui_parts: [],
    };
    if (showUser) {
      flushSync(() => setMessages((prev) => [...prev, placeholder]));
    }
    let reply = '';
    try {
      await sendPublicShare(
        token,
        keycloak?.authenticated ? keycloak : null,
        body,
        {
          onDelta: (delta) => {
            reply += delta;
            setMessages((prev) => {
              const next = [...prev];
              const i = next.length - 1;
              if (i >= 0 && next[i].role === 'assistant') {
                next[i] = { ...next[i], content: reply, streaming: true, actions_enabled: true };
                return next;
              }
              next.push({ ...placeholder, content: reply });
              return next;
            });
          },
          onUiPart: (part) => {
            setMessages((prev) => {
              const next = [...prev];
              const i = next.length - 1;
              if (i >= 0 && next[i].role === 'assistant') {
                next[i] = {
                  ...next[i],
                  streaming: true,
                  actions_enabled: true,
                  ui_parts: filterDisplayableUiParts(upsertUiPart(next[i].ui_parts, part)),
                };
                return next;
              }
              next.push({ ...placeholder, ui_parts: filterDisplayableUiParts([part]) });
              return next;
            });
          },
          onUiPatch: (patch) => setMessages((prev) => applyUiPatchToMessages(prev, patch)),
        },
      );
      setMessages((prev) => prev.map((item) => (
        item.streaming ? { ...item, streaming: false, content: reply || item.content } : item
      )));
    } catch (err) {
      antdMessage.error(err?.message || 'Could not send');
      if (/limit/i.test(err?.message || '')) setShareComposer('limit');
      if (/closed/i.test(err?.message || '')) setShareComposer('closed');
      setMessages((prev) => {
        const next = [...prev];
        const i = next.length - 1;
        if (i >= 0 && next[i].streaming) {
          next[i] = {
            ...next[i],
            streaming: false,
            isError: true,
            content: next[i].content || `Error: ${err?.message || 'Could not send'}`,
          };
        }
        return next;
      });
    } finally {
      setIsSending(false);
    }
  }, [shareMode, isSending, shareComposer, token, keycloak]);

  const handleSend = useCallback(async (text, uiAction = null) => {
    if (shareMode) {
      if (!text?.trim()) return;
      await sendShare({
        content: text,
        ...(uiAction ? {
          ui_action_id: uiAction.actionId,
          ui_action_payload: uiAction.payload,
          ui_part_id: uiAction.partId,
          resume_pending_tool: Boolean(uiAction.resumePendingTool),
        } : {}),
      }, { showUser: true });
      return;
    }
    if (!text.trim() || isSending || !assistant) return;

    const routing = assistant.router_mode === true;
    if (!routing && enginePath.useAgent && assistant.is_local) {
      antdMessage.warning('Connected tools require a cloud model on this assistant.');
      return;
    }

    const engineMeta = buildEngineMetadata(toggles, enginePath);
    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
    flushSync(() => {
      setMessages((prev) => {
        const cleared = prev.map((m, idx) => (
          idx === prev.length - 1 && m.ui ? { ...m, ui: { ...m.ui, actions: [] } } : m
        ));
        return [...cleared, userMsg];
      });
    });
    setInput('');
    setIsSending(true);
    const saved = await persistMessage('user', text, false, null, engineMeta);
    if (saved) userMsg.id = saved.id;

    const assistantPlaceholder = {
      role: 'assistant',
      content: '',
      streaming: true,
      timestamp: new Date().toISOString(),
    };
    flushSync(() => {
      setMessages((prev) => [...prev, assistantPlaceholder]);
    });

    let replyAccum = '';
    let lastPipelineTrace = null;
    let lastModuleUi = null;
    let lastUiParts = [];
    let lastAwaitingUser = false;
    let lastConsulted = null;
    let lastPin = null;
    const noteRouterStatus = (specialist) => {
      if (!specialist?.name) return;
      setMessages((prev) => {
        const next = [...prev];
        const i = next.length - 1;
        if (i >= 0 && next[i].streaming) {
          const names = next[i].routerNames || [];
          const routerNames = names.includes(specialist.name) ? names : [...names, specialist.name];
          next[i] = { ...next[i], routerNames, routerStatus: routerNames.join(' · ') };
        }
        return next;
      });
    };
    try {
      const history = [...messages, userMsg].map(m => ({ role: m.role, content: m.content }));

      if (routing) {
        await sendChatMessageStream(keycloak, assistantId, history, {
          sessionId,
          onRouterStatus: noteRouterStatus,
          onDelta: (delta) => {
            replyAccum += delta;
            setMessages((prev) => {
              const next = [...prev];
              const i = next.length - 1;
              if (i >= 0 && next[i].role === 'assistant' && next[i].streaming) {
                next[i] = { ...next[i], content: (next[i].content || '') + delta };
              }
              return next;
            });
          },
          onPipelineTrace: (pt) => {
            if (pt == null) return;
            lastPipelineTrace = pt;
          },
          onModuleUi: (ui) => {
            lastModuleUi = ui;
            applyUiToLastMessage(ui);
          },
          onUiPart: (part) => {
            lastUiParts = filterDisplayableUiParts(upsertUiPart(lastUiParts, part));
            setMessages((prev) => {
              const next = [...prev];
              const i = next.length - 1;
              if (i >= 0 && next[i].streaming) {
                next[i] = {
                  ...next[i],
                  ui_parts: filterDisplayableUiParts(upsertUiPart(next[i].ui_parts, part)),
                };
              }
              return next;
            });
          },
          onDone: (done) => {
            lastPipelineTrace = done?.pipeline_trace ?? lastPipelineTrace;
            lastConsulted = done?.consulted || null;
            if (done?.ui_parts) lastUiParts = filterDisplayableUiParts(done.ui_parts);
            if (done?.pinned_specialist_id) {
              lastPin = {
                pinned_specialist_id: done.pinned_specialist_id,
                pinned_agent_session_id: done.pinned_agent_session_id ?? null,
              };
            }
            if (done?.module_ui) {
              lastModuleUi = done.module_ui;
              applyUiToLastMessage(done.module_ui);
            }
            if (done?.awaiting_user) lastAwaitingUser = true;
          },
          ...(uiAction
            ? {
                uiActionId: uiAction.actionId,
                uiActionPayload: uiAction.payload,
                uiPartId: uiAction.partId,
                resumePendingTool: Boolean(uiAction.resumePendingTool),
              }
            : {}),
        });
      } else if (enginePath.useAgent) {
        const priorMessages = [...messages, userMsg]
          .filter((m) => (m.role === 'user' || m.role === 'assistant') && (m.content || '').trim())
          .map((m) => ({ role: m.role, content: m.content }));
        const res = await sendAgentChatStream(keycloak, assistantId, {
          message: text,
          messages: priorMessages,
          ...agentRequestFields(),
          ...(uiAction
            ? {
                ui_action_id: uiAction.actionId,
                ui_action_payload: uiAction.payload,
                ui_part_id: uiAction.partId,
              }
            : {}),
        });
        await consumeChatNdjsonStream(res, {
          onDelta: (delta) => {
            replyAccum += delta;
            setMessages((prev) => {
              const next = [...prev];
              const i = next.length - 1;
              if (i >= 0 && next[i].streaming) {
                next[i] = { ...next[i], content: (next[i].content || '') + delta };
              }
              return next;
            });
          },
          onPipelineTrace: (pt) => {
            if (pt == null) return;
            lastPipelineTrace = pt;
            setMessages((prev) => {
              const next = [...prev];
              const i = next.length - 1;
              if (i >= 0 && next[i].streaming) {
                next[i] = { ...next[i], pipeline_trace: pt };
              }
              return next;
            });
          },
          onUiPart: (part) => {
            lastUiParts = filterDisplayableUiParts(upsertUiPart(lastUiParts, part));
            setMessages((prev) => {
              const next = [...prev];
              const i = next.length - 1;
              if (i >= 0 && next[i].streaming) {
                next[i] = {
                  ...next[i],
                  ui_parts: filterDisplayableUiParts(upsertUiPart(next[i].ui_parts, part)),
                };
              }
              return next;
            });
          },
          onUiPatch: (patch) => {
            setMessages((prev) => applyUiPatchToMessages(prev, patch));
          },
          onToolCall: () => {},
          onToolResult: () => {},
          onDone: (done) => {
            if (done?.agent_session_id) setAgentSessionId(done.agent_session_id);
            if (done?.ui_parts) lastUiParts = filterDisplayableUiParts(done.ui_parts);
            if (done?.pipeline_trace) lastPipelineTrace = done.pipeline_trace;
            if (done?.awaiting_user) lastAwaitingUser = true;
          },
        });
      } else {
      const streamOpts = {
        onDelta: (delta) => {
          replyAccum += delta;
          setMessages(prev => {
            const next = [...prev];
            const i = next.length - 1;
            if (i >= 0 && next[i].role === 'assistant' && next[i].streaming) {
              next[i] = { ...next[i], content: (next[i].content || '') + delta };
            }
            return next;
          });
        },
        onPipelineTrace: (pt) => {
          if (pt == null) return;
          lastPipelineTrace = pt;
          setMessages(prev => {
            const next = [...prev];
            const i = next.length - 1;
            if (i >= 0 && next[i].role === 'assistant' && next[i].streaming) {
              next[i] = { ...next[i], pipeline_trace: pt };
            }
            return next;
          });
        },
        onModuleUi: (ui) => {
          lastModuleUi = ui;
          applyUiToLastMessage(ui);
        },
        onDone: (done) => {
          lastPipelineTrace = done?.pipeline_trace ?? lastPipelineTrace;
          if (done?.module_ui) {
            lastModuleUi = done.module_ui;
            applyUiToLastMessage(done.module_ui);
          }
          if (lastPipelineTrace) {
            setMessages(prev => {
              const next = [...prev];
              const i = next.length - 1;
              if (i >= 0 && next[i].role === 'assistant' && next[i].streaming) {
                next[i] = { ...next[i], pipeline_trace: lastPipelineTrace };
              }
              return next;
            });
          }
        },
        sessionId,
      };
      if (enginePath.useRag && selectedCollection) {
        await sendRAGChatMessageStream(keycloak, {
          collectionId: selectedCollection,
          messages: history,
          model: assistant.model,
          assistantId: assistant?.id,
        }, streamOpts);
      } else {
        await sendChatMessageStream(keycloak, assistantId, history, streamOpts);
      }
      }
      setMessages(prev => {
        const next = [...prev];
        const i = next.length - 1;
        if (next[i]?.streaming) {
          const parts = lastUiParts.length ? lastUiParts : next[i].ui_parts;
          next[i] = {
            ...next[i],
            streaming: false,
            awaitingUser: (routing || enginePath.useAgent) ? lastAwaitingUser : false,
            ui_parts: filterDisplayableUiParts(parts || []),
            consulted: lastConsulted || next[i].consulted || null,
            routerStatus: null,
          };
        }
        return next;
      });
      const replyText = ((routing || enginePath.useAgent) && lastAwaitingUser)
        ? ''
        : (replyAccum || '…');
      const meta = buildMessageMetadata(
        lastModuleUi,
        lastUiParts.length ? lastUiParts : undefined,
        {
          ...engineMeta,
          ...(lastConsulted ? { consulted: lastConsulted } : {}),
          ...(lastPin || {}),
        },
      );
      const savedA = replyText
        ? await persistMessage('assistant', replyText, false, lastPipelineTrace, meta)
        : (lastPipelineTrace || lastUiParts.length
          ? await persistMessage('assistant', '', false, lastPipelineTrace, meta)
          : null);
      if (savedA) {
        setMessages(prev => {
          const next = [...prev];
          const i = next.length - 1;
          if (next[i]?.role === 'assistant') {
            next[i] = {
              ...next[i],
              id: savedA.id,
              pipeline_trace: lastPipelineTrace ?? next[i].pipeline_trace,
            };
          }
          return next;
        });
      }
    } catch (error) {
      const friendly = error?.message || 'An error occurred';
      const errMsg = {
        role: 'assistant',
        content: `Error: ${friendly}`,
        timestamp: new Date().toISOString(),
        isError: true,
        fullError: error?.fullError || String(error),
        streaming: false,
      };
      setMessages(prev => {
        const next = [...prev];
        const i = next.length - 1;
        if (i >= 0 && next[i]?.streaming) {
          next[i] = errMsg;
        } else {
          next.push(errMsg);
        }
        return next;
      });
      await persistMessage('assistant', errMsg.content, true);
    } finally { setIsSending(false); }
  }, [
    isSending, assistant, messages, selectedCollection, keycloak, assistantId, persistMessage,
    sessionId, applyUiToLastMessage, buildMessageMetadata, toolsEnabled, agentSessionId,
    enginePath, collectionAttached, toggles, agentRequestFields, shareMode, sendShare,
  ]);

  const handleUiAction = useCallback(async (action) => {
    if (shareMode) {
      if (shareComposer !== 'open' || isSending) return;
      if (isResumeAction(action) || isGateCancelAction(action.actionId)) dismissGate();
      const fields = {
        ui_action_id: action.actionId,
        ui_action_payload: action.payload,
        ui_part_id: action.partId,
        resume_pending_tool: Boolean(action.resumePendingTool || isResumeAction(action)),
      };
      if (action.mode === 'patch' || isResumeAction(action)) {
        await sendShare({ content: '', ...fields }, { showUser: false });
        return;
      }
      await sendShare({
        content: formatUiActionLabel(action.actionId, action.payload || {}),
        ...fields,
      }, { showUser: true });
      return;
    }
    if (!assistant) return;
    const { actionId, mode, payload, partId } = action;

    if (isResumeAction(action) || isGateCancelAction(actionId)) {
      dismissGate();
    }

    if (mode === 'patch') {
      const runPatch = async () => {
        const res = await sendAgentChatStream(keycloak, assistantId, {
          message: '',
          ...agentRequestFields(),
          ui_action_id: actionId,
          ui_action_payload: payload,
          ui_part_id: partId,
        });
        await consumeChatNdjsonStream(res, {
          onUiPart: (part) => {
            setMessages((prev) => {
              const next = [...prev];
              const i = next.length - 1;
              if (i >= 0 && next[i].role === 'assistant') {
                next[i] = {
                  ...next[i],
                  ui_parts: upsertUiPart(next[i].ui_parts, part),
                };
              }
              return next;
            });
          },
          onUiPatch: (patch) => {
            setMessages((prev) => applyUiPatchToMessages(prev, patch));
          },
          onDone: (done) => {
            if (done?.agent_session_id) setAgentSessionId(done.agent_session_id);
          },
        });
      };

      if (patchDepthRef.current === 0) setIsSending(true);
      patchDepthRef.current += 1;
      patchQueueRef.current = patchQueueRef.current
        .then(runPatch)
        .catch((e) => {
          antdMessage.error(e?.message || 'Action failed');
        })
        .finally(() => {
          patchDepthRef.current -= 1;
          if (patchDepthRef.current === 0) setIsSending(false);
        });
      return;
    }
    if (isSending) return;
    if (isResumeAction(action)) {
      setIsSending(true);
      try {
        const priorMessages = messages
          .filter((m) => (m.role === 'user' || m.role === 'assistant') && (m.content || '').trim())
          .map((m) => ({ role: m.role, content: m.content }));
        markResuming();
        const res = await sendAgentChatStream(keycloak, assistantId, buildResumePayload(action, {
          priorMessages,
          agentRequestFields,
          agentSessionId,
        }));
        let replyAccum = '';
        let lastPipelineTrace = null;
        await consumeChatNdjsonStream(res, {
          onDelta: (d) => {
            replyAccum += d;
            setMessages((prev) => {
              const next = [...prev];
              const i = next.length - 1;
              if (i >= 0 && next[i].role === 'assistant') {
                next[i] = { ...next[i], content: (next[i].content || '') + d, streaming: true };
              }
              return next;
            });
          },
          onPipelineTrace: (pt) => {
            if (pt == null) return;
            lastPipelineTrace = pt;
            setMessages((prev) => {
              const next = [...prev];
              const i = next.length - 1;
              if (i >= 0 && next[i].role === 'assistant') {
                next[i] = { ...next[i], pipeline_trace: pt };
              }
              return next;
            });
          },
          onUiPart: (part) => {
            setMessages((prev) => {
              const next = [...prev];
              const i = next.length - 1;
              if (i >= 0 && next[i].role === 'assistant') {
                next[i] = {
                  ...next[i],
                  ui_parts: filterDisplayableUiParts(
                    upsertUiPart(next[i].ui_parts, part),
                  ),
                };
              }
              return next;
            });
          },
          onDone: (done) => {
            if (done?.agent_session_id) setAgentSessionId(done.agent_session_id);
          },
        });
        setMessages((prev) => {
          const next = [...prev];
          const i = next.length - 1;
          if (i >= 0 && next[i].role === 'assistant') {
            next[i] = {
              ...next[i],
              streaming: false,
              content: replyAccum || next[i].content || '',
              pipeline_trace: lastPipelineTrace ?? next[i].pipeline_trace,
              ui_parts: filterDisplayableUiParts(
                (next[i].ui_parts || []).filter(
                  (p) => (p.component || '') !== 'user_gate_bar',
                ),
              ),
            };
          }
          return next;
        });
        if (replyAccum) {
          await persistMessage('assistant', replyAccum, false, lastPipelineTrace);
        }
      } catch (e) {
        antdMessage.error(e?.message || 'Confirm failed');
      } finally {
        setIsSending(false);
      }
      return;
    }
    const label = formatUiActionLabel(actionId, payload);
    handleSend(label, { actionId, payload, partId });
  }, [assistant, isSending, keycloak, assistantId, handleSend, persistMessage, agentRequestFields, messages, agentSessionId, dismissGate, markResuming, isResumeAction, isGateCancelAction, shareMode, shareComposer, sendShare]);

  const handleRedo = useCallback(async () => {
    if (isSending) return;
    const lastUserIdx = messages.map(m => m.role).lastIndexOf('user');
    if (lastUserIdx === -1) return;
    const text = messages[lastUserIdx].content;
    setMessages(prev => prev.slice(0, lastUserIdx));
    setTimeout(() => handleSend(text), 50);
  }, [messages, isSending, handleSend]);

  const handleFeedback = useCallback(async (idx, feedback) => {
    const msg = messages[idx];
    if (!msg?.id) return;
    setMessages(prev => prev.map((m, i) => i === idx ? { ...m, feedback } : m));
    try {
      if (feedback) await submitMessageFeedback(keycloak, { messageId: msg.id, assistantId: assistant?.id, feedback });
      else await removeMessageFeedback(keycloak, msg.id);
    } catch (_) { /* */ }
  }, [messages, keycloak, assistant]);

  const handleClearHistory = useCallback(async () => {
    if (isSending) return;
    try {
      await clearChatHistory(keycloak, assistantId);
      setMessages([]);
      setAgentSessionId(null);
      const next = createChatSessionId();
      setSessionId(next);
      try { sessionStorage.removeItem(`focusml-chat-session:${assistantId}`); } catch (_) { /* */ }
      setClearHistoryModalVisible(false);
      antdMessage.success('Chat history cleared');
    } catch (_) {
      antdMessage.error('Failed to clear history');
    }
  }, [keycloak, assistantId, isSending]);

  const startNewChat = useCallback(() => {
    if (isSending) return;
    const next = createChatSessionId();
    setSessionId(next);
    setAgentSessionId(null);
    setMessages([]);
    setInput('');
    try { sessionStorage.setItem(`focusml-chat-session:${assistantId}`, next); } catch (_) { /* */ }
  }, [assistantId, isSending]);

  const startShareChat = useCallback(async () => {
    if (isSending || !token) return;
    try {
      const auth = keycloak?.authenticated ? keycloak : null;
      await startNewPublicShare(token, auth);
      const data = await readPublicShare(token, auth);
      setShareState(data.state || 'not_found');
      setChatOnly(Boolean(data.chat_only));
      setShareComposer(data.composer || 'closed');
      setAllowEmbed(Boolean(data.allow_embed));
      setAssistant(data.assistant ? { ...data.assistant, router_mode: false } : null);
      setMessages((data.messages || []).map((message) => ({
        role: message.role,
        content: message.content,
        ui: message.ui || null,
        ui_parts: message.ui_parts || null,
        actions_enabled: Boolean(message.actions_enabled),
      })));
      setInput('');
    } catch (err) {
      antdMessage.error(err.message || 'Could not start a new chat');
    }
  }, [isSending, token, keycloak, setChatOnly]);

  const goChannels = useCallback(() => {
    navigate(`/assistants/${assistantId}/channels`);
  }, [navigate, assistantId]);

  const openStatistics = useCallback(async () => {
    setStatsModalOpen(true);
    setStatsLoading(true);
    setStatsData(null);
    try {
      const [start, end] = statsDateRange;
      const data = await getAssistantStatistics(keycloak, assistantId, start.toDate(), end.toDate());
      setStatsData(data);
    } catch (e) {
      antdMessage.error(e?.message || 'Failed to load statistics');
      setStatsModalOpen(false);
    } finally {
      setStatsLoading(false);
    }
  }, [keycloak, assistantId, statsDateRange]);

  const handleStatsDateRangeChange = useCallback(
    (dates) => {
      if (!dates?.[0] || !dates?.[1]) return;
      setStatsDateRange(dates);
      setStatsLoading(true);
      getAssistantStatistics(keycloak, assistantId, dates[0].toDate(), dates[1].toDate())
        .then(setStatsData)
        .catch((e) => antdMessage.error(e?.message || 'Failed to load statistics'))
        .finally(() => setStatsLoading(false));
    },
    [keycloak, assistantId]
  );

  const handleStartStop = useCallback(async () => {
    if (!assistant) return;
    const running = assistant.status === 'running';
    try {
      if (running) {
        await stopAssistant(keycloak, assistantId);
        antdMessage.success('Assistant stopped');
      } else {
        await runAssistant(keycloak, assistantId);
        antdMessage.success('Assistant started');
      }
      const data = await fetchAssistant(keycloak, assistantId);
      setAssistant(data);
    } catch (e) {
      antdMessage.error(e?.message || 'Request failed');
    }
  }, [assistant, keycloak, assistantId]);

  const conversationStarters = conversationStartersFor(assistant);

  const showPipelineTraceUi = !shareMode
    && pipelineTraceVisibility?.pipeline_trace_enabled
    && pipelineTraceVisibility?.pipeline_trace_show_platform_chat;

  if (assistantLoading || (shareMode && shareState === 'loading')) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - var(--header-height, 64px))' }}><Spin size="large" /></div>;
  }
  if (shareMode && shareState === 'login') {
    return (
      <div className="chat-page-root" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <div style={{ fontSize: 22, fontWeight: 600, marginBottom: 16 }}>Sign in to view this chat</div>
          <Button type="primary" onClick={() => keycloak.login({ redirectUri: window.location.href })}>Sign in</Button>
        </div>
      </div>
    );
  }
  if (shareMode && shareState === 'open' && !allowEmbed && window.self !== window.top) {
    return (
      <div className="chat-page-root" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 16, color: '#6b7280' }}>This chat can’t be embedded.</div>
      </div>
    );
  }
  if (shareMode && shareState !== 'open') {
    return (
      <div className="chat-page-root" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 22, fontWeight: 600 }}>Not found</div>
      </div>
    );
  }
  if (!assistant) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - var(--header-height, 64px))', color: '#6b7280' }}>Assistant not found</div>;

  const assistantAvatarSrc = assistantDisplayImageSrc(assistant);

  const isLastMsg = (idx) => idx === messages.length - 1;

  return (
    <div className="chat-page-root">

      <div className="chat-header-bar" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 28px',
        borderBottom: '1px solid #ebebeb',
        background: '#fff',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ChatMessageAvatar src={assistantAvatarSrc} icon={<RobotOutlined />} size={38} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, color: '#1a1d29', lineHeight: 1.3 }}>{assistant.name}</div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 1 }}>{assistant.model}</div>
          </div>
        </div>
        {shareMode ? (
          <Dropdown
            menu={{
              items: [
                {
                  key: 'new-chat',
                  label: (
                    <span>
                      <PlusOutlined style={{ marginRight: 8 }} />
                      Start new chat
                    </span>
                  ),
                  onClick: () => startShareChat(),
                },
              ],
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              style={{ color: '#9ca3af' }}
              aria-label="Menu"
            />
          </Dropdown>
        ) : null}
        {!shareMode && <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {!assistant.router_mode && availableCollections.length > 0 && (
            <Tooltip title="Attach document collection to this conversation">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <DatabaseOutlined style={{ color: '#9ca3af' }} />
                <Switch
                  size="small"
                  checked={collectionAttached}
                  onChange={(checked) => {
                    setCollectionAttached(checked);
                    if (checked && !selectedCollection && assistant?.data_collection_id) {
                      setSelectedCollection(assistant.data_collection_id);
                    }
                  }}
                />
              </span>
            </Tooltip>
          )}
          {collectionAttached && (
            <Select
              placeholder="Collection"
              style={{ width: 180 }}
              loading={collectionsLoading}
              value={selectedCollection || undefined}
              onChange={(v) => setSelectedCollection(v || null)}
              options={availableCollections.map((c) => ({ value: c.id, label: c.name }))}
              allowClear
              onClear={() => setSelectedCollection(null)}
              disabled={availableCollections.length === 0}
            />
          )}
          {!assistant.router_mode && hasAgentTools && (
            <Tooltip title="Use connected MCP / HTTP tools">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <ApiOutlined style={{ color: '#9ca3af' }} />
                <Switch
                  size="small"
                  checked={toolsEnabled}
                  onChange={setToolsEnabled}
                />
              </span>
            </Tooltip>
          )}
          {!assistant.router_mode && showRagStrategy && (
            <Select
              size="small"
              style={{ width: 150 }}
              value={ragStrategy}
              onChange={setRagStrategy}
              options={[
                { value: RAG_STRATEGY_LEGACY, label: 'Retrieve first' },
                { value: RAG_STRATEGY_AGENT_LED, label: 'Agent search' },
              ]}
            />
          )}
          {assistant?.owner && keycloak?.tokenParsed?.sub === assistant.owner ? (
            <Button icon={<ShareAltOutlined />} onClick={() => setShareOpen(true)}>
              Share
            </Button>
          ) : null}
          <Dropdown
            menu={{
              items: [
                {
                  key: 'new-chat',
                  label: (
                    <span>
                      <PlusOutlined style={{ marginRight: 8 }} />
                      Start new chat
                    </span>
                  ),
                  onClick: () => startNewChat(),
                },
                {
                  key: 'channels',
                  label: (
                    <span>
                      <ApiOutlined style={{ marginRight: 8 }} />
                      Channels
                    </span>
                  ),
                  onClick: () => goChannels(),
                },
                {
                  key: 'stats',
                  label: (
                    <span>
                      <BarChartOutlined style={{ marginRight: 8 }} />
                      Statistics
                    </span>
                  ),
                  onClick: () => openStatistics(),
                },
                {
                  key: 'edit',
                  label: (
                    <span>
                      <EditOutlined style={{ marginRight: 8 }} />
                      Edit assistant
                    </span>
                  ),
                  onClick: () => navigate(`/assistants/${assistantId}/edit`),
                },
                {
                  key: 'run',
                  label: (
                    <span>
                      {assistant.status === 'running' ? (
                        <StopOutlined style={{ marginRight: 8 }} />
                      ) : (
                        <PlayCircleOutlined style={{ marginRight: 8 }} />
                      )}
                      {assistant.status === 'running' ? 'Stop assistant' : 'Start assistant'}
                    </span>
                  ),
                  onClick: () => handleStartStop(),
                },
                ...(messages.length > 0 ? [{
                  key: 'divider',
                  type: 'divider',
                }] : []),
                ...(messages.length > 0 ? [{
                  key: 'clear',
                  label: (
                    <span>
                      <DeleteOutlined style={{ marginRight: 8 }} />
                      Clear chat history
                    </span>
                  ),
                  danger: true,
                  onClick: () => {
                    setClearHistoryModalVisible(true);
                  },
                }] : []),
              ],
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              style={{ color: '#9ca3af' }}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        </div>}
      </div>

      <div className="chat-messages-scroll">
        <div style={{ maxWidth: 820, margin: '0 auto', padding: '20px 24px' }}>

          {messages.length === 0 && !isSending && (
            <div className="chat-empty">
              <ChatMessageAvatar src={assistantAvatarSrc} icon={<RobotOutlined />} size={52} bg="#d1d5db" />
              <div className="chat-empty-title">Chat with {assistant.name}</div>
              {conversationStarters.length > 0 && (!shareMode || shareComposer === 'open') ? (
                <div className="chat-starters">
                  {conversationStarters.map((text, index) => (
                    <button
                      key={`${index}-${text}`}
                      type="button"
                      className="chat-starter"
                      onClick={() => {
                        setInput(text);
                        inputRef.current?.focus?.();
                      }}
                    >
                      {text}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="chat-empty-hint">
                  {selectedCollection ? `Ask questions about ${collections.find(c => c.id === selectedCollection)?.name}` : 'Send a message to get started'}
                </div>
              )}
            </div>
          )}

          {/* Message list */}
          {messages.map((msg, idx) => {
            const isUser = msg.role === 'user';
            const isAssistant = msg.role === 'assistant';
            const last = isLastMsg(idx);
            const actionsLive = last && (!shareMode || msg.actions_enabled);
            const showTraceUi = showPipelineTraceUi && isAssistant && !msg.isError;
            const hasStreamText = Boolean(String(msg.content || '').trim());
            const interactionPart = isAssistant ? findGatePart(msg, last) : null;
            const partsReady = !msg.streaming || hasStreamText;
            const streamUiParts = partsReady ? (msg.ui_parts || []) : [];
            const hasBubbleParts = bubbleUiParts(streamUiParts).length > 0;
            const needsFallback = isAssistant && !msg.streaming && assistantMessageNeedsFallback({
              ...msg,
              ui_parts: filterDisplayableUiParts(streamUiParts),
            });
            const bubbleContent = isAssistant
              ? (hasStreamText ? msg.content : (needsFallback ? inferAssistantFallback(msg) : msg.content))
              : msg.content;
            const showBubble = msg.isError
              || Boolean(String(bubbleContent || '').trim())
              || (partsReady && hasBubbleParts)
              || Boolean(interactionPart);
            const showStreamingTyping = isAssistant
              && msg.streaming
              && !hasStreamText
              && !interactionPart
              && !msg.routerStatus
              && !(msg.pipeline_trace?.steps?.length);

            return (
              <div
                key={idx}
                ref={last ? lastMessageTopRef : undefined}
                style={{ display: 'flex', flexDirection: isUser ? 'row-reverse' : 'row', alignItems: 'flex-start', gap: 10, marginBottom: 20 }}
              >
                {/* Avatar */}
                <div style={{ paddingTop: 2 }}>
                  {isUser
                    ? <ChatMessageAvatar icon={<UserOutlined />} bg="#3b82f6" size={30} />
                    : <ChatMessageAvatar src={assistantAvatarSrc} icon={<RobotOutlined />} size={30} />}
                </div>

                {/* Bubble column: pipeline summary above bubble when complete */}
                <div className={`chat-message-column${isAssistant ? ' chat-message-column--assistant' : ''}`}>
                  {showTraceUi && msg.pipeline_trace && !msg.streaming && !interactionPart && (
                    <PipelineTracePanel variant="aboveBubble" trace={msg.pipeline_trace} />
                  )}
                  {isAssistant && msg.streaming && !hasStreamText && msg.routerStatus && (
                    <div className="chat-router-status">{msg.routerStatus}</div>
                  )}
                  {showStreamingTyping && (
                    <div className="chat-typing-indicator" aria-hidden>
                      <span /><span /><span />
                    </div>
                  )}
                  {isAssistant && msg.streaming && !hasStreamText && !interactionPart && (
                    <StreamingPipelineStatus trace={msg.pipeline_trace} hasAnswerText={hasStreamText} />
                  )}
                  {interactionPart && (
                    <div className="ui-write-permission-window">
                      <UserGateBar
                        props={interactionPart.props}
                        actions={interactionPart.actions}
                        partId={interactionPart.part_id}
                        onAction={handleUiAction}
                        disabled={isSending || !actionsLive}
                        dir={chatDir}
                      />
                    </div>
                  )}
                  {showBubble && (
                  <div style={{
                    padding: '10px 16px',
                    borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    fontSize: 15,
                    lineHeight: 1.65,
                    background: msg.isError ? '#fef2f2' : isUser ? '#3b82f6' : '#f3f4f6',
                    color: msg.isError ? '#dc2626' : isUser ? '#fff' : '#1a1d29',
                    border: msg.isError ? '1px solid #fecaca' : 'none',
                    boxShadow: '0 1px 2px rgba(0,0,0,.04)',
                    wordBreak: 'break-word',
                  }}>
                    {isAssistant && !msg.isError ? (
                      <MessageWithParts
                        content={bubbleContent}
                        uiParts={streamUiParts}
                        onAction={handleUiAction}
                        disabled={isSending || !actionsLive}
                        frozen={!actionsLive}
                        dir={chatDir}
                        markdownComponent={
                          msg.streaming
                            ? (showTraceUi ? StreamingAssistantMarkdown : AssistantMarkdown)
                            : AssistantMarkdown
                        }
                      />
                    ) : (
                      <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                    )}
                    {msg.isError && msg.fullError && (
                      <Button type="link" size="small" icon={<CodeOutlined />}
                        onClick={() => { console.log('Full error:', msg.fullError); antdMessage.info('Error details printed to console (F12)'); }}
                        style={{ padding: 0, height: 'auto', fontSize: 12, color: '#dc2626', marginTop: 4 }}>
                        View error details
                      </Button>
                    )}
                    {isAssistant && !msg.isError && msg.ui?.actions?.length > 0
                      && last && !msg.streaming && (
                      <MessageInlineActions
                        actions={msg.ui.actions || []}
                        disabled={isSending}
                        onAction={handleInlineAction}
                      />
                    )}
                  </div>
                  )}
                  {isAssistant && !msg.streaming && Array.isArray(msg.consulted) && msg.consulted.length > 0 && (
                    <div className="chat-consulted-line">
                      {msg.consulted.map((item) => item.name).filter(Boolean).join(' · ')}
                    </div>
                  )}
                  {showBubble && isAssistant && !msg.streaming && (() => {
                    const mailTarget = resolveOtpMailTarget(msg);
                    if (!mailTarget) return null;
                    return <OtpOpenMailButton email={mailTarget.email} />;
                  })()}

                  {/* Timestamp — trigger only as wide as the label (see Chat.css) */}
                  {msg.timestamp && (
                  <div className={isUser ? 'chat-time-tooltip-row chat-time-tooltip-row--user' : 'chat-time-tooltip-row'}>
                    <Tooltip title={fullDateTime(msg.timestamp)}>
                      <span className="chat-time-tooltip-wrap">{relativeTime(msg.timestamp)}</span>
                    </Tooltip>
                  </div>
                  )}

                  {/* Action bar — assistant messages get full actions; user messages get copy only */}
                  {isAssistant && !msg.isError && !msg.streaming && (
                    <MessageActions
                      msg={msg}
                      isLast={last}
                      showAlways={last}
                      onFeedback={shareMode ? undefined : (fb) => handleFeedback(idx, fb)}
                      onCopy={() => { copyText(msg.content); antdMessage.success('Copied', 1.5); }}
                      onRedo={shareMode ? undefined : handleRedo}
                    />
                  )}
                  {isUser && (
                    <MessageActions
                      msg={msg}
                      isLast={false}
                      showAlways={false}
                      onCopy={() => { copyText(msg.content); antdMessage.success('Copied', 1.5); }}
                    />
                  )}
                </div>
              </div>
            );
          })}

          {/* Typing indicator (hidden while streamed reply is already shown) */}
          {isSending && !messages.some((m) => m.streaming) && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 20 }}>
              <div style={{ paddingTop: 2 }}>
                <ChatMessageAvatar src={assistantAvatarSrc} icon={<RobotOutlined />} size={30} />
              </div>
              <div className="chat-message-column chat-message-column--assistant">
                <div className="chat-typing-indicator" aria-hidden>
                  <span /><span /><span />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input bar */}
      <div className="chat-input-bar" style={{ padding: '10px 28px 16px', background: '#fafbfc' }}>
        <div className="chat-input-pill" style={{
          maxWidth: 820, margin: '0 auto', background: '#fff',
          border: '1px solid #e5e7eb', borderRadius: 24,
          padding: '4px 6px 4px 20px',
          display: 'flex', alignItems: 'flex-end',
          boxShadow: '0 1px 4px rgba(0,0,0,.06)',
          transition: 'border-color 200ms ease, box-shadow 200ms ease',
        }}>
          <Input.TextArea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onPressEnter={e => {
              if (shareMode && shareComposer !== 'open') return;
              if (!e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                if (input.trim()) handleSend(input);
              }
            }}
            placeholder={
              shareMode && shareComposer === 'limit'
                ? 'This shared chat has reached its limit.'
                : shareMode && shareComposer === 'closed'
                  ? 'This conversation is closed.'
                  : selectedCollection
                    ? `Ask about ${collections.find(c => c.id === selectedCollection)?.name}…`
                    : `Message ${assistant.name}…`
            }
            autoSize={{ minRows: 1, maxRows: 5 }}
            style={{ flex: 1, border: 'none', boxShadow: 'none', resize: 'none', fontSize: 15, lineHeight: 1.5, padding: '8px 0', background: 'transparent' }}
            disabled={isSending || assistantLoading || (shareMode && shareComposer !== 'open')}
          />
          <Button
            type="text" shape="circle" icon={<SendOutlined style={{ fontSize: 16 }} />}
            onClick={() => input.trim() && !(shareMode && shareComposer !== 'open') && handleSend(input)}
            loading={isSending}
            disabled={!input.trim() || isSending || (shareMode && shareComposer !== 'open')}
            style={{
              width: 36, height: 36, flexShrink: 0, marginBottom: 2,
              color: input.trim() ? '#3b82f6' : '#d1d5db',
              transition: 'color 200ms ease',
            }}
          />
        </div>
        {!shareMode && engineStatusLine && (
          <div style={{ maxWidth: 820, margin: '6px auto 0', fontSize: 11, color: '#9ca3af', textAlign: 'center' }}>
            {engineStatusLine}
          </div>
        )}
        {!shareMode && toolsEnabled && !collectionAttached && hasAgentTools && (
          <div style={{ maxWidth: 820, margin: '4px auto 0', fontSize: 11, color: '#b45309', textAlign: 'center' }}>
            Document search is off, attach a collection to search uploaded documents.
          </div>
        )}
      </div>

      {!shareMode && <ChatShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        keycloak={keycloak}
        assistantId={assistantId}
        sessionId={sessionId}
        canShare={Boolean(assistant?.owner && keycloak?.tokenParsed?.sub === assistant.owner)}
      />}
      {/* Clear chat history modal */}
      <Modal
        title="Clear chat history"
        open={clearHistoryModalVisible}
        onOk={handleClearHistory}
        onCancel={() => setClearHistoryModalVisible(false)}
        okText="Clear"
        okType="danger"
        okButtonProps={{ disabled: isSending }}
        cancelText="Cancel"
      >
        <p>
          This will permanently delete all messages in this chat
          {toolsEnabled ? ' and reset any in-progress agent session.' : '.'}
        </p>
      </Modal>

      <Modal
        title={assistant ? `Statistics — ${assistant.name}` : 'Statistics'}
        open={statsModalOpen}
        onCancel={() => {
          setStatsModalOpen(false);
          setStatsData(null);
        }}
        footer={null}
        width={1040}
        destroyOnClose
        styles={{
          body: { maxHeight: '85vh', overflowY: 'auto', overflowX: 'hidden', padding: '12px 16px' },
          content: { maxWidth: 'min(1040px, calc(100vw - 32px))' },
        }}
      >
        <AssistantStatisticsContent
          statsData={statsData}
          loading={statsLoading}
          dateRange={statsDateRange}
          onDateRangeChange={handleStatsDateRangeChange}
        />
      </Modal>
    </div>
  );
}

export default Chat;
